import { toast } from 'sonner';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
    AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { useDeleteCalendarEvent } from '../../hooks/useCalendar';
import { destructiveCopy } from '../../lib/calendar/eventCapabilities';
import { errorText } from '../../lib/apiErrors';

/**
 * Delete an event / cancel a booking, confirmed in the app's own dialog (#680)
 * rather than `window.confirm`. The copy states the consequence — cancelling a
 * booking emails the guest, which the native dialog made easy to miss.
 *
 * Pattern: pages/FormDataPage.jsx — controlled `open`, dismissal blocked while
 * the request is in flight, and the action preventDefault()s so the dialog
 * stays open until the mutation settles.
 */
export function DestructiveEventDialog({ event, onCancel, onDone }) {
    const deleteEvent = useDeleteCalendarEvent();
    const copy = event ? destructiveCopy(event) : null;
    const pending = deleteEvent.isPending;

    const run = async (e) => {
        e.preventDefault();
        try {
            await deleteEvent.mutateAsync({ eventId: event.event_id, source: event.source, bookingId: event.booking_id || null });
            toast.success(copy.done);
            onDone?.();
        } catch (err) {
            toast.error(errorText(err, copy.failed));
        }
    };

    return (
        <AlertDialog open={!!event} onOpenChange={(open) => { if (!open && !pending) onCancel(); }}>
            <AlertDialogContent className="rounded-[14px]" data-testid="event-destructive-dialog">
                {copy && (
                    <>
                        <AlertDialogHeader>
                            <AlertDialogTitle>{copy.title}</AlertDialogTitle>
                            <AlertDialogDescription>{copy.body}</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel disabled={pending} data-testid="event-destructive-cancel">Keep it</AlertDialogCancel>
                            <AlertDialogAction
                                onClick={run}
                                disabled={pending}
                                className="bg-[#A3382B] hover:bg-[#8A2E23] text-white"
                                data-testid="event-destructive-confirm"
                            >
                                {pending ? 'Working…' : copy.confirm}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </>
                )}
            </AlertDialogContent>
        </AlertDialog>
    );
}
